import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import PaginationControls from '@/components/shared/PaginationControls';
import DateCell from '@/components/table/DateCell';
import { SortableColumnHeader } from '@/components/table/SortableColumnHeader';
import TableSkeleton from '@/components/table/TableSkeleton';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import { useDashboardTableQueryState } from '@/components/table/useDashboardTableQueryState';
import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip } from '@/components/ui/tooltip';
import { useCancelInvitationsAction } from '@/hooks/actions';
import { useCopyInvitationLink } from '@/components/invitations/use-copy-invitation-link';
import { useListAffiliateInvitationsQuery } from '@/hooks/queries/use-list-affiliate-invitations-query';
import useInvitationSelectionStore from '@/stores/invitation-selection-store';
import { Badge, Flex, HStack, IconButton, Table, Text, VStack } from '@chakra-ui/react';
import {
  InvitationStatus,
  PlatformEnum,
  type BulkInvitationActionFilters,
  type BulkInvitationActionRequest,
  type InvitationOutreachKind,
  type DashboardTableSortState,
  type InvitationTableSortBy,
  type ProgramInvitation,
} from '@fluenceur/types';
import {
  DeliveryStatusBadge,
  toDisplayDeliveryStatus,
} from '@/components/delivery/DeliveryStatusBadge';
import { getPlatformProfileUrl } from '@/components/creator-discovery/utils/creator-formatters';
import CreatorAvatar from '@/components/creators/CreatorAvatar';
import { useOpenAffiliateSurface } from '@/hooks/use-open-affiliate-surface';
import InvitationBulkActionBar from './InvitationBulkActionBar';
import InvitationBulkEmailModal, {
  type InvitationBulkEmailTarget,
} from './InvitationBulkEmailModal';
import InvitationOutreachEmailModal from './InvitationOutreachEmailModal';
import InvitationSelectAllBanner from './InvitationSelectAllBanner';
import InviteAffiliatesButton from './InviteAffiliatesButton';

import { parseAsString, useQueryState } from 'nuqs';
import type { CSSProperties } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InvitationFilters from './InvitationFilters';
import InvitationsEmptyState from './InvitationsEmptyState';
import InvitationStatsCards from './InvitationStatsCards';
import { Link as LinkIcon, Mail, RefreshDouble, Trash, WarningTriangle } from 'iconoir-react';

interface InvitationsTableProps {
  programId?: string;
}

const SELECTION_COLUMN_WIDTH = '48px';
const INVITEE_COLUMN_WIDTH = '250px';
const EMAIL_COLUMN_WIDTH = '220px';
const PROGRAM_COLUMN_WIDTH = '220px';
const METHOD_COLUMN_WIDTH = '130px';
const STATUS_COLUMN_WIDTH = '170px';
const DATE_COLUMN_WIDTH = '130px';
// Room for all four row actions (copy link, email, resend, cancel) at xs size.
const ACTIONS_COLUMN_WIDTH = '160px';
const INVITATION_DEFAULT_SORT = {
  sortBy: 'createdAt',
  sortDirection: 'desc',
} as const satisfies DashboardTableSortState<InvitationTableSortBy>;
const INVITATION_TABLE_SORT_OPTIONS = [
  'invitee',
  'email',
  'program',
  'method',
  'status',
  'joinedAt',
  'createdAt',
  'expiresAt',
] as const satisfies readonly [InvitationTableSortBy, ...InvitationTableSortBy[]];
const INVITATION_PROGRAM_SCOPED_SORT_OPTIONS = [
  'invitee',
  'email',
  'method',
  'status',
  'joinedAt',
  'createdAt',
  'expiresAt',
] as const satisfies readonly [InvitationTableSortBy, ...InvitationTableSortBy[]];

const getStatusBadge = (invitation: ProgramInvitation, t: (key: string) => string) => {
  const statusConfig: Record<InvitationStatus, { colorPalette: string; textKey: string }> = {
    [InvitationStatus.PENDING]: { colorPalette: 'orange', textKey: 'invitations.status.pending' },
    [InvitationStatus.CLICKED]: { colorPalette: 'blue', textKey: 'invitations.status.clicked' },
    [InvitationStatus.JOINED]: { colorPalette: 'green', textKey: 'invitations.status.joined' },
    [InvitationStatus.EXPIRED]: { colorPalette: 'red', textKey: 'invitations.status.expired' },
    [InvitationStatus.UNDELIVERABLE]: {
      colorPalette: 'red',
      textKey: 'invitations.status.undeliverable',
    },
  };

  if (invitation.status === InvitationStatus.UNDELIVERABLE) {
    const tooltipKey =
      invitation.undeliverableReason === 'complained'
        ? 'invitations.undeliverable.tooltip.complained'
        : 'invitations.undeliverable.tooltip.bounced';
    const colorPalette = invitation.undeliverableReason === 'complained' ? 'orange' : 'red';
    return (
      <Tooltip content={t(tooltipKey)} openDelay={200} portalled positioning={{ placement: 'top' }}>
        <Badge colorPalette={colorPalette} variant="subtle">
          {t('invitations.status.undeliverable')}
        </Badge>
      </Tooltip>
    );
  }

  const config = statusConfig[invitation.status];
  return (
    <Badge colorPalette={config.colorPalette} variant="subtle">
      {t(config.textKey)}
    </Badge>
  );
};

/**
 * How the message that carried this invitation fared, under the invitation's
 * own status. The brand watches its creators from this screen and the status
 * alone never said whether the message left, arrived, or failed.
 *
 * Nothing is rendered when the invitation carries no delivery state: a cold
 * invitation the brand sent itself has no application behind it, and that
 * silence must read as an absence of information, never as a success and never
 * as a failure.
 */
const renderDeliveryBadge = (invitation: ProgramInvitation) => {
  const delivery = invitation.messageDelivery;
  if (!delivery) return null;

  // A delivery attached to this very invitation yet marked `not_required` means
  // no new message was needed: the invitation already existed and was reused.
  // The Brand Space applications screen reads the same row the same way.
  const status = delivery.status === 'not_required' ? 'reused' : delivery.status;

  return <DeliveryStatusBadge status={toDisplayDeliveryStatus(status)} />;
};

const InvitationsTable: React.FC<InvitationsTableProps> = ({ programId }) => {
  const { t } = useTranslation();

  // Read URL state for filters - using namespaced parameters
  const [search] = useQueryState('invitationSearch', parseAsString.withDefault(''));
  const [status] = useQueryState('invitationStatus', parseAsString.withDefault('all'));
  const tableState = useDashboardTableQueryState<InvitationTableSortBy>({
    namespace: 'invitation',
    sortByOptions: programId
      ? INVITATION_PROGRAM_SCOPED_SORT_OPTIONS
      : INVITATION_TABLE_SORT_OPTIONS,
    defaultSort: INVITATION_DEFAULT_SORT,
    defaultPerPage: 20,
  });
  const {
    query: tableQuery,
    sort: tableSort,
    setPage: setTablePage,
    setSort: setTableSort,
  } = tableState;

  // Opens the affiliate surface (unified contact drawer or legacy affiliate drawer)
  const openAffiliateSurface = useOpenAffiliateSurface();

  // Bulk selection (checkboxes + select-all-matching), shared with the action bar.
  const selectedIds = useInvitationSelectionStore((s) => s.selectedIds);
  const selectAllMatching = useInvitationSelectionStore((s) => s.selectAllMatching);
  const matchingTotal = useInvitationSelectionStore((s) => s.matchingTotal);
  const toggleSelection = useInvitationSelectionStore((s) => s.toggleSelection);
  const toggleSelectAll = useInvitationSelectionStore((s) => s.toggleSelectAll);
  const setMatchingTotal = useInvitationSelectionStore((s) => s.setMatchingTotal);
  const clearSelection = useInvitationSelectionStore((s) => s.clearSelection);

  // Pending confirmation for cancel (per-row uses a single id, the bar uses the
  // whole selection or the filter).
  const [pendingCancel, setPendingCancel] = useState<{
    request: BulkInvitationActionRequest;
    count: number;
  } | null>(null);

  // Mailbox email to one invitation: a follow-up from the resend action, a
  // plain email from the envelope.
  const [outreachTarget, setOutreachTarget] = useState<{
    invitation: ProgramInvitation;
    kind: InvitationOutreachKind;
  } | null>(null);
  // Grouped mailbox email: the selection is frozen when the composer opens.
  const [bulkEmailTarget, setBulkEmailTarget] = useState<InvitationBulkEmailTarget | null>(null);

  // Build filters for the query
  const filters = {
    search: search || undefined,
    status:
      (status as 'pending' | 'clicked' | 'joined' | 'expired' | 'undeliverable' | 'all') || 'all',
    sortBy: tableQuery.sortBy,
    sortDirection: tableQuery.sortDirection,
    programId,
    page: tableQuery.page,
    perPage: tableQuery.perPage,
  };

  // Track if we've ever loaded data to avoid skeleton on filter changes
  const hasLoadedDataRef = useRef(false);

  // Use the list affiliate invitations query directly
  const { data, isFetching, error } = useListAffiliateInvitationsQuery({
    filters,
    enabled: true,
  });

  // Mutation for bulk cancel (one or many invitations)
  const cancelMutation = useCancelInvitationsAction();
  const handleCopyInvitationLink = useCopyInvitationLink();

  const invitations = data?.data || [];
  const paginationMeta = data?.meta;

  // Keep the select-all-matching count in sync with the filter total.
  useEffect(() => {
    setMatchingTotal(paginationMeta?.total ?? 0);
  }, [paginationMeta?.total, setMatchingTotal]);

  // Reset the selection only when the filter changes (the matching set changes).
  // Paginating keeps the selection, so a "select all matching" choice survives
  // navigating across pages.
  useEffect(() => {
    clearSelection();
  }, [search, status, programId, clearSelection]);

  // Mark as loaded once we get any data
  if (data && !hasLoadedDataRef.current) {
    hasLoadedDataRef.current = true;
  }

  // Only show skeleton if we're fetching AND have never loaded data before
  const isInitialLoading = isFetching && !hasLoadedDataRef.current;

  const pageIds = invitations.map((invitation) => invitation.id);
  const allPageSelected =
    selectAllMatching || (pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id)));
  const somePageSelected = !allPageSelected && pageIds.some((id) => selectedIds.has(id));
  const selectionCount = selectAllMatching ? matchingTotal : selectedIds.size;

  const buildBulkRequest = (): BulkInvitationActionRequest => {
    if (selectAllMatching) {
      const filter: BulkInvitationActionFilters = {
        status: status !== 'all' ? (status as BulkInvitationActionFilters['status']) : undefined,
        search: search || undefined,
        programId,
      };
      return { filter };
    }
    return { ids: Array.from(selectedIds) };
  };

  // Per-row cancel (single id, still confirmed)
  const handleCancelInvitation = (invitation: ProgramInvitation) => {
    setPendingCancel({ request: { ids: [invitation.id] }, count: 1 });
  };

  const handleBulkCancel = () => {
    setPendingCancel({ request: buildBulkRequest(), count: selectionCount });
  };

  const handleBulkEmail = (kind: InvitationOutreachKind) => {
    setBulkEmailTarget({
      kind,
      selection: buildBulkRequest(),
      recipientCount: selectionCount,
      mayIncludeExpired: status === 'all' || status === 'expired',
    });
  };

  // The selection stays under the recap (the dialog is layered above the bar,
  // closing the bar would dismiss it) and is cleared once a send was closed.
  const handleCloseBulkEmail = ({ sent }: { sent: boolean }) => {
    setBulkEmailTarget(null);
    if (sent) {
      clearSelection();
    }
  };

  const handleConfirmCancel = () => {
    if (!pendingCancel) return;
    cancelMutation.mutate(pendingCancel.request);
    setPendingCancel(null);
    clearSelection();
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setTablePage(newPage);
  };

  // Handle view affiliate details in drawer
  const handleViewAffiliate = (userId: string) => {
    openAffiliateSurface(userId);
  };

  const handleOpenOutreach = (invitation: ProgramInvitation, kind: InvitationOutreachKind) => {
    setOutreachTarget({ invitation, kind });
  };

  const handleCloseOutreach = () => {
    setOutreachTarget(null);
  };

  // Show error state
  if (error) {
    return (
      <EmptyState
        title={t('invitations.errorLoading')}
        description={error instanceof Error ? error.message : t('common.error')}
        icon={<WarningTriangle width={22} height={22} />}
      />
    );
  }

  if (isInitialLoading) {
    const skeletonColumns = programId
      ? [
          { width: SELECTION_COLUMN_WIDTH },
          { width: INVITEE_COLUMN_WIDTH },
          { width: EMAIL_COLUMN_WIDTH },
          { width: METHOD_COLUMN_WIDTH },
          { width: STATUS_COLUMN_WIDTH },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: ACTIONS_COLUMN_WIDTH },
        ]
      : [
          { width: SELECTION_COLUMN_WIDTH },
          { width: INVITEE_COLUMN_WIDTH },
          { width: EMAIL_COLUMN_WIDTH },
          { width: PROGRAM_COLUMN_WIDTH },
          { width: METHOD_COLUMN_WIDTH },
          { width: STATUS_COLUMN_WIDTH },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: DATE_COLUMN_WIDTH, type: 'date' as const },
          { width: ACTIONS_COLUMN_WIDTH },
        ];

    return (
      <VStack gap={6} w="full" align="flex-start">
        <InvitationStatsCards programId={programId} />
        <InvitationFilters />
        <TableSkeleton
          rows={5}
          columns={skeletonColumns}
          stickyColumns={2}
          stickyLastColumn
          firstColumnWidth={SELECTION_COLUMN_WIDTH}
          secondColumnWidth={INVITEE_COLUMN_WIDTH}
          lastColumnWidth={ACTIONS_COLUMN_WIDTH}
        />
      </VStack>
    );
  }

  // A narrowed-down list and an untouched one are different situations: only the
  // search box and the status dropdown can hide rows, so they alone decide
  // whether the filter bar has to stay on screen for the user to recover.
  const hasActiveFilters = search !== '' || status !== 'all';

  // Nothing was ever invited: the stats cards, the filter bar, the column
  // headers and the pagination bar would all be empty chrome. Show the message
  // on its own instead.
  if (!invitations.length && !hasActiveFilters) {
    return <InvitationsEmptyState action={<InviteAffiliatesButton programId={programId} />} />;
  }

  // The filter matches nothing: keep the filter bar so the user can clear it,
  // and keep the stats since they count every invitation, not just the matches.
  if (!invitations.length) {
    return (
      <VStack gap={6} w="full" align="flex-start">
        <InvitationStatsCards programId={programId} />
        <InvitationFilters />
        <InvitationsEmptyState filtered />
      </VStack>
    );
  }

  const tableStyle = {
    '--table-min-width': programId ? '1330px' : '1550px',
  } as CSSProperties;

  const sortedColumnCellProps = (column: InvitationTableSortBy) => ({
    bg: tableSort.sortBy === column ? 'gray.50/70' : undefined,
  });

  const renderMethodBadge = (invitation: ProgramInvitation) => {
    const { invitee } = invitation;
    const isTikTok = invitee.platform === PlatformEnum.TikTok;
    const isEmailInvitation =
      invitee.type !== 'creator' && !invitee.email?.endsWith('@temp.fluenceur.com');
    const sourceLabel = isEmailInvitation
      ? t('invitations.source.email')
      : isTikTok
        ? t('invitations.source.tiktok')
        : t('invitations.source.instagram');
    const sourceColor = isEmailInvitation ? 'blue' : isTikTok ? 'gray' : 'pink';

    return (
      <Badge size="xs" variant="subtle" colorPalette={sourceColor}>
        {sourceLabel}
      </Badge>
    );
  };

  const renderProgramBadge = (invitation: ProgramInvitation) => (
    <Badge size="xs" colorPalette={invitation.program.isDeleted ? 'red' : 'gray'}>
      {invitation.program.isDeleted ? `${t('invitations.badges.deleted')} ` : ''}
      {invitation.program.name}
    </Badge>
  );

  return (
    <VStack gap={6} w="full" align="flex-start">
      <InvitationStatsCards programId={programId} />
      <InvitationFilters />

      <InvitationSelectAllBanner pageIds={pageIds} matchingTotal={paginationMeta?.total ?? 0} />

      <TableScrollArea
        style={tableStyle}
        opacity={isFetching && !isInitialLoading ? 0.6 : 1}
        transition="opacity 0.15s"
        pointerEvents={isFetching && !isInitialLoading ? 'none' : 'auto'}
      >
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader
                width={SELECTION_COLUMN_WIDTH}
                aria-label={t('invitations.columns.select')}
                {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
              >
                <Checkbox
                  size="sm"
                  checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                  onCheckedChange={() =>
                    selectAllMatching ? clearSelection() : toggleSelectAll(pageIds)
                  }
                />
              </Table.ColumnHeader>
              <SortableColumnHeader
                column="invitee"
                label={t('invitations.columns.invitee')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={INVITEE_COLUMN_WIDTH}
                {...stickyColumnProps(INVITEE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}
              />
              <SortableColumnHeader
                column="email"
                label={t('invitations.columns.email')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={EMAIL_COLUMN_WIDTH}
              />
              {!programId && (
                <SortableColumnHeader
                  column="program"
                  label={t('invitations.columns.program')}
                  sort={tableSort}
                  defaultSort={INVITATION_DEFAULT_SORT}
                  onSortChange={setTableSort}
                  width={PROGRAM_COLUMN_WIDTH}
                />
              )}
              <SortableColumnHeader
                column="method"
                label={t('invitations.columns.method')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={METHOD_COLUMN_WIDTH}
              />
              <SortableColumnHeader
                column="status"
                label={t('invitations.columns.status')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={STATUS_COLUMN_WIDTH}
              />
              <SortableColumnHeader
                column="joinedAt"
                label={t('invitations.columns.joined')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={DATE_COLUMN_WIDTH}
              />
              <SortableColumnHeader
                column="createdAt"
                label={t('invitations.columns.created')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={DATE_COLUMN_WIDTH}
              />
              <SortableColumnHeader
                column="expiresAt"
                label={t('invitations.columns.expires')}
                sort={tableSort}
                defaultSort={INVITATION_DEFAULT_SORT}
                onSortChange={setTableSort}
                width={DATE_COLUMN_WIDTH}
              />
              <Table.ColumnHeader
                width={ACTIONS_COLUMN_WIDTH}
                textAlign="end"
                aria-label={t('invitations.columns.actions')}
                {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}
              />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invitations.map((invitation) => {
              const { invitee } = invitation;
              const isTempEmail = invitee.email?.endsWith('@temp.fluenceur.com');
              const isCreatorInvitation = invitee.type === 'creator' || isTempEmail;
              const isClickable = invitation.status === 'Joined' && invitee.id;
              const profileUrl =
                getPlatformProfileUrl(
                  invitee.platform ?? undefined,
                  invitee.platformUsername ?? undefined
                ) ?? '';
              const displayedEmail = isTempEmail ? '' : invitee.email;
              const canSendOutreach =
                !!invitee.email &&
                !isTempEmail &&
                !invitation.program.isDeleted &&
                [
                  InvitationStatus.PENDING,
                  InvitationStatus.CLICKED,
                  InvitationStatus.EXPIRED,
                ].includes(invitation.status);
              return (
                <Table.Row key={invitation.id} _hover={{ bg: 'gray.50' }}>
                  <Table.Cell
                    width={SELECTION_COLUMN_WIDTH}
                    {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
                  >
                    <Checkbox
                      size="sm"
                      checked={selectAllMatching || selectedIds.has(invitation.id)}
                      disabled={selectAllMatching}
                      onCheckedChange={() => toggleSelection(invitation.id)}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                    />
                  </Table.Cell>
                  <Table.Cell
                    {...stickyColumnProps(INVITEE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}
                    {...sortedColumnCellProps('invitee')}
                  >
                    {isCreatorInvitation && invitee.platformUsername ? (
                      <CreatorAvatar
                        creatorId={invitee.platformUsername}
                        creatorName={invitee.name || invitee.platformUsername}
                        fallbackSrc={invitee.profilePictureUrl ?? undefined}
                        username={invitee.platformUsername}
                        profileUrl={profileUrl}
                        showDetails
                        onNameClick={
                          isClickable ? () => handleViewAffiliate(invitee.id!) : undefined
                        }
                        size="xs"
                        lazyFetch
                      />
                    ) : (
                      <Flex align="center">
                        <Avatar
                          name={invitee.name || invitee.email}
                          size="xs"
                          mr={2}
                          colorPalette="primary"
                        />
                        <VStack align="flex-start" gap={0.5}>
                          <Text
                            cursor={isClickable ? 'pointer' : undefined}
                            color="dark"
                            lineClamp={1}
                            _hover={isClickable ? { textDecoration: 'underline' } : undefined}
                            onClick={
                              isClickable ? () => handleViewAffiliate(invitee.id!) : undefined
                            }
                          >
                            {invitee.name || invitee.email}
                          </Text>
                        </VStack>
                      </Flex>
                    )}
                  </Table.Cell>
                  <Table.Cell {...sortedColumnCellProps('email')}>
                    <Text fontSize="sm" color={displayedEmail ? 'fg' : 'fg.muted'} lineClamp={1}>
                      {displayedEmail || '-'}
                    </Text>
                  </Table.Cell>
                  {!programId && (
                    <Table.Cell {...sortedColumnCellProps('program')}>
                      {renderProgramBadge(invitation)}
                    </Table.Cell>
                  )}
                  <Table.Cell {...sortedColumnCellProps('method')}>
                    {renderMethodBadge(invitation)}
                  </Table.Cell>
                  <Table.Cell {...sortedColumnCellProps('status')}>
                    <VStack align="flex-start" gap={1}>
                      {getStatusBadge(invitation, t)}
                      {renderDeliveryBadge(invitation)}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell {...sortedColumnCellProps('joinedAt')}>
                    <DateCell dateString={invitation.joinedAt} />
                  </Table.Cell>
                  <Table.Cell {...sortedColumnCellProps('createdAt')}>
                    <DateCell dateString={invitation.createdAt} />
                  </Table.Cell>
                  <Table.Cell {...sortedColumnCellProps('expiresAt')}>
                    <DateCell dateString={invitation.expiresAt} />
                  </Table.Cell>
                  <Table.Cell {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}>
                    <HStack gap={1} justify="flex-end">
                      {/* Copy link - the server exposes the link only while the
                          invitation is still usable (pending or clicked) */}
                      {invitation.invitationLink && (
                        <Tooltip
                          content={t('invitations.tooltips.copyLink')}
                          portalled
                          positioning={{ placement: 'top' }}
                        >
                          <IconButton
                            variant="ghost"
                            size="xs"
                            rounded="md"
                            aria-label={t('invitations.copyLink')}
                            colorPalette="gray"
                            onClick={() => void handleCopyInvitationLink(invitation)}
                          >
                            <LinkIcon width={20} height={20} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canSendOutreach && (
                        <Tooltip
                          content={t('invitations.tooltips.sendOutreach')}
                          portalled
                          positioning={{ placement: 'top' }}
                        >
                          <IconButton
                            variant="ghost"
                            size="xs"
                            rounded="md"
                            aria-label={t('invitations.sendOutreach')}
                            colorPalette="gray"
                            onClick={() => handleOpenOutreach(invitation, 'outreach')}
                          >
                            <Mail width={20} height={20} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Follow-up from the brand's mailbox, on the same rows
                          as the plain email */}
                      {canSendOutreach && (
                        <Tooltip
                          content={t('invitations.tooltips.canResend')}
                          portalled
                          positioning={{ placement: 'top' }}
                        >
                          <IconButton
                            variant="ghost"
                            size="xs"
                            rounded="md"
                            aria-label={t('invitations.resendInvitation')}
                            colorPalette="gray"
                            onClick={() => handleOpenOutreach(invitation, 'follow_up')}
                          >
                            <RefreshDouble width={20} height={20} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Cancel Button - Only shown for cancellable invitations */}
                      {invitation.canCancel && (
                        <Tooltip
                          content={t('invitations.tooltips.canCancel')}
                          portalled
                          positioning={{ placement: 'top' }}
                        >
                          <IconButton
                            variant="ghost"
                            size="xs"
                            rounded="md"
                            aria-label={t('invitations.cancelInvitation')}
                            colorPalette="red"
                            onClick={() => handleCancelInvitation(invitation)}
                            disabled={cancelMutation.isPending}
                          >
                            <Trash width={20} height={20} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </TableScrollArea>

      {/* Pagination Controls */}
      {paginationMeta && (
        <PaginationControls
          pagination={{
            total: paginationMeta.total,
            perPage: paginationMeta.perPage,
            currentPage: paginationMeta.currentPage,
            lastPage: paginationMeta.lastPage,
            from: (paginationMeta.currentPage - 1) * paginationMeta.perPage + 1,
            to: Math.min(paginationMeta.currentPage * paginationMeta.perPage, paginationMeta.total),
          }}
          onPageChange={handlePageChange}
          isLoading={isFetching}
        />
      )}

      {/* Bulk action bar (mailbox email / mailbox follow-up / cancel selected) */}
      <InvitationBulkActionBar
        onEmail={() => handleBulkEmail('outreach')}
        onResend={() => handleBulkEmail('follow_up')}
        onCancel={handleBulkCancel}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!pendingCancel}
        title={t('invitations.bulkCancel.confirmTitle', { count: pendingCancel?.count ?? 0 })}
        description={t('invitations.bulkCancel.confirmDescription', {
          count: pendingCancel?.count ?? 0,
        })}
        cancelText={t('common.back')}
        confirmText={t('invitations.bulkActionBar.cancel')}
        confirmColorPalette="red"
        isLoading={cancelMutation.isPending}
        onConfirm={handleConfirmCancel}
        onCancel={() => setPendingCancel(null)}
      />

      <InvitationOutreachEmailModal
        invitation={outreachTarget?.invitation ?? null}
        kind={outreachTarget?.kind ?? 'outreach'}
        isOpen={!!outreachTarget}
        onClose={handleCloseOutreach}
      />

      <InvitationBulkEmailModal target={bulkEmailTarget} onClose={handleCloseBulkEmail} />
    </VStack>
  );
};

export default InvitationsTable;
